import { useState } from 'react';
import { createSearchParams, useNavigate } from 'react-router-dom';
import {
  CloseCircleOutlined,
  CopyOutlined,
  DeleteOutlined,
  EditOutlined,
  ExportOutlined,
  EyeOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import {
  Access,
  AccessRule,
  AccessRuleConflictView,
  AccessRuleException,
  AccessRuleOwner,
  AccessRuleType,
  AccessSource,
  Department,
  EmployeeType,
  User,
} from '@prisma/client';
import { Button, ButtonProps, Descriptions, DescriptionsProps, MenuProps, ModalProps, Result, Space, Spin } from 'antd';
import { DropdownButtonProps } from 'antd/es/dropdown';
import Table, { ColumnsType } from 'antd/es/table';

import { Role } from '@app/domain-interfaces';
import { Auth, Config } from '@app/web-components';

import { EmployeeTypeRenderer } from '../../../common/atoms/employee-type-renderer';
import { useDeleteAccessRule } from '../data/access-rule';

import { useCanEditAccessRule } from './use-can-edit-access-rule';

type UseAccessRuleActionsProps = {
  onCreate: (record: AccessRuleWithDetail | null) => string;
  onEdit: (record: AccessRuleWithDetail) => string;
};

export type AccessRuleWithDetail = AccessRule & {
  access: Access;
  conflict: AccessRuleConflictView;
  owners: (AccessRuleOwner & { user: User })[];
  exceptions: (AccessRuleException & { user: User })[];
  department?: Department;
  conflictAccess?: Access;
};

export const accessRuleIncludes = {
  access: true,
  conflict: true,
  owners: { include: { user: true } },
  exceptions: { include: { user: true } },
  department: true,
  conflictAccess: true,
};

export const useAccessRuleActions = ({ onCreate, onEdit }: UseAccessRuleActionsProps) => {
  const { canEditAccessRule } = useCanEditAccessRule();
  const [deleteRule, setDeleteRule] = useState<AccessRuleWithDetail | undefined>(undefined);
  const deleter = useDeleteAccessRule();
  const navigate = useNavigate();
  const { promoteUrl } = Config.useConfig();
  const [rule, setRule] = useState<AccessRuleWithDetail | undefined>(undefined);
  const { user } = Auth.useUserAuth();

  const handleOnCopy = (record: AccessRuleWithDetail) =>
    navigate({
      pathname: onCreate(record),
      search: createSearchParams({
        ruleType: `${record.conflict.ruleType === AccessRuleType.ACCESS_CONFLICT ? 'access' : 'department'}-conflict`,
        id: record.id.toString(),
      }).toString(),
    });

  const handleOnEdit = (record: AccessRuleWithDetail) => record && navigate(onEdit(record));

  const handleOnPromote = (record: AccessRuleWithDetail) => {
    const payload: { [key: string]: string } = {
      s: record.access.accessSource,
      a: record.access.accessSource === AccessSource.IEM ? record.access.name : record.access.externalId,
      t: record.employeeType === EmployeeType.INTEL ? 'B' : 'G',
      o: record.owners.map((x) => x.user.enterpriseId).join(','),
    };

    if (record.employeeSubtype) {
      payload['u'] = record.employeeSubtype;
    }

    if (record.exceptions?.length) {
      payload['e'] = record.exceptions
        .map((x) => `${x.user.enterpriseId},${encodeURIComponent(x.justification)}`)
        .join(';');
    }

    if (record.conflictAccess) {
      payload['cs'] = record.conflictAccess.accessSource;
      payload['c'] =
        record.conflictAccess.accessSource === AccessSource.IEM
          ? record.conflictAccess.name
          : record.conflictAccess.externalId;
    }

    if (record.department) {
      payload['d'] = record.department.externalId;
    }

    const searchParams = new URLSearchParams(payload);
    window.location.href = `${promoteUrl}/admin/access-rules/promote?${searchParams.toString()}`;
  };

  const getDescriptionItems = (item?: AccessRuleWithDetail): DescriptionsProps['items'] => [
    {
      key: 'access',
      children: item?.access.name,
      label: 'Access',
    },
    {
      key: 'conflict',
      children: item?.conflict?.name,
      label: item?.conflict.ruleType === AccessRuleType.ACCESS_CONFLICT ? 'Conflict Access' : 'Department',
    },
    {
      key: 'employeeType',
      children: <EmployeeTypeRenderer employeeType={item?.employeeType} />,
      label: 'Employee Type',
    },
    { key: 'employeeSubtype', children: item?.employeeSubtype, label: 'Employee Subtype' },
  ];

  const getCreateButtonProps = (type: AccessRuleType, props?: ButtonProps): ButtonProps => ({
    children: `Add ${type === 'ACCESS_CONFLICT' ? 'Access' : 'Department'} Conflict`,
    icon: <PlusOutlined />,
    ...props,
    onClick: () =>
      navigate({
        pathname: onCreate(null),
        search: createSearchParams({
          ruleType: `${type === AccessRuleType.ACCESS_CONFLICT ? 'access' : 'department'}-conflict`,
        }).toString(),
      }),
  });

  const getDeleteModalProps = (props?: ModalProps): ModalProps => {
    const items = getDescriptionItems(deleteRule);

    const onCancel = () => {
      setDeleteRule(undefined);
      deleter.reset();
    };

    return {
      centered: true,
      title: 'Are you sure you want to delete this access rule?',
      ...props,
      children: deleter.isPending ? (
        <Spin style={{ width: '100%' }} />
      ) : deleter.isSuccess ? (
        <Result
          status="success"
          title="Successfully deleted access rule"
          subTitle={<Descriptions bordered size="small" layout="horizontal" column={2} items={items} />}
        />
      ) : deleter.isError ? (
        <Result status="error" title="Error occured when deleting access rule" subTitle={deleter.error.message} />
      ) : (
        <Descriptions bordered size="small" layout="horizontal" column={2} items={items} />
      ),
      cancelButtonProps: { style: deleter.isSuccess ? { display: 'none' } : undefined, disabled: deleter.isPending },
      okButtonProps: {
        icon: deleter.isSuccess ? <CloseCircleOutlined /> : <DeleteOutlined />,
        disabled: deleter.isPending,
      },
      okText: deleter.isIdle || deleter.isPending ? 'Delete' : deleter.isError ? 'Retry' : 'Close',
      okType: deleter.isSuccess ? undefined : 'danger',
      open: !!deleteRule,
      width: 800,
      onCancel,
      onOk: () => {
        if (deleter.isIdle || deleter.isError) {
          deleteRule && deleter.mutate(deleteRule.id);
        } else {
          onCancel();
        }
      },
    };
  };

  const getDetailsModalProps = (props?: ModalProps): ModalProps => {
    const footer = [
      <Button key="close" onClick={() => setRule(undefined)} icon={<CloseCircleOutlined />} type="primary">
        Close
      </Button>,
    ];

    // Copy allowed for Rule and System admins only, not Bundle Admins
    if (user?.roles[Role.RULE_ADMIN]) {
      promoteUrl &&
        footer.unshift(
          <Button icon={<ExportOutlined />} key="promote" onClick={() => rule && handleOnPromote(rule)}>
            Promote
          </Button>,
        );

      footer.unshift(
        <Button icon={<CopyOutlined />} key="copy" onClick={() => rule && handleOnCopy(rule)}>
          Copy
        </Button>,
      );
    }

    if (canEditAccessRule(rule)) {
      footer.unshift(
        <Button icon={<EditOutlined />} key="edit" onClick={() => rule && handleOnEdit(rule)}>
          Edit
        </Button>,
      );

      footer.unshift(
        <Button icon={<DeleteOutlined />} key="delete" onClick={() => rule && setDeleteRule(rule)}>
          Delete
        </Button>,
      );
    }

    const items = getDescriptionItems(rule);

    const ownerColumns: ColumnsType<AccessRuleOwner & { user: User }> = [
      { title: 'WWID', dataIndex: ['user', 'enterpriseId'] },
      { title: 'Name', dataIndex: ['user', 'name'] },
    ];

    const exceptionsColumns: ColumnsType<AccessRuleException & { user: User }> = [
      { title: 'WWID', dataIndex: ['user', 'enterpriseId'] },
      { title: 'Name', dataIndex: ['user', 'name'] },
      { title: 'Justification', dataIndex: 'justification' },
    ];

    return {
      centered: true,
      title: 'Access Rule Details',
      width: 1100,
      ...props,
      children: (
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          <Descriptions bordered size="small" layout="horizontal" column={2} items={items} />
          <Table rowKey="id" caption="Owners" columns={ownerColumns} dataSource={rule?.owners} size="small" />
          <Table
            rowKey="id"
            caption="Exceptions"
            columns={exceptionsColumns}
            dataSource={rule?.exceptions}
            size="small"
          />
        </Space>
      ),
      footer,
      open: !!rule,
      onCancel: () => setRule(undefined),
    };
  };

  const getDropdownButtonProps = (record: AccessRuleWithDetail, props?: DropdownButtonProps): DropdownButtonProps => {
    const items: MenuProps['items'] = [];

    // Copy allowed for Rule and System admins only, not Bundle Admins
    if (user?.roles[Role.RULE_ADMIN]) {
      promoteUrl &&
        items.unshift({
          label: 'Promote',
          key: 'promote',
          icon: <ExportOutlined />,
        });
      items.unshift({
        label: 'Copy',
        key: 'copy',
        icon: <CopyOutlined />,
      });
    }

    if (canEditAccessRule(record)) {
      items.unshift({
        label: 'Edit',
        key: 'edit',
        icon: <EditOutlined />,
      });

      items.push({
        label: 'Delete',
        key: 'delete',
        icon: <DeleteOutlined />,
        danger: true,
      });
    }

    const handleMenuOnClick = (e: { key: string }) => {
      switch (e.key) {
        case 'edit':
          handleOnEdit(record);
          break;
        case 'copy':
          handleOnCopy(record);
          break;
        case 'delete':
          setDeleteRule(record);
          break;
        case 'promote': {
          handleOnPromote(record);
          break;
        }
        default:
          console.log(`Cannot handle key: ${e.key}.`);
          break;
      }
    };

    return {
      size: 'small',
      ...props,
      children: (
        <Space>
          <EyeOutlined />
          Details
        </Space>
      ),
      menu: { items, onClick: handleMenuOnClick },
      onClick: () => setRule(record),
    };
  };

  return { getCreateButtonProps, getDeleteModalProps, getDropdownButtonProps, getDetailsModalProps };
};
